export const CHART_ZOOM_TOOLBOX_ICON_PATHS = {
  zoomIn:
    'M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z',
  zoomOut:
    'M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z',
  zoomSelect:
    'M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z',
  pan: 'M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c.22-.19.49-.29.79-.29.22 0 .42.06.6.16.04.01 4.31 2.46 4.31 2.46V4c0-.83.67-1.5 1.5-1.5S11 3.17 11 4v7h1V1.5c0-.83.67-1.5 1.5-1.5S15 .67 15 1.5V11h1V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h1V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5z',
  home: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
} as const;

export const CHART_ZOOM_INSIDE_ID = 'chartZoomInside';

export type TChartZoomInteractionMode = 'zoom' | 'pan';

export type TChartZoomRange = {
  start: number;
  end: number;
};

const MIN_ZOOM_SPAN = 5;

export const FULL_ZOOM_RANGE: TChartZoomRange = { start: 0, end: 100 };

type TDataZoomOptionItem = {
  id?: string;
  type?: string;
  start?: number;
  end?: number;
};

export type TChartWithZoom = {
  getOption: () => { dataZoom?: TDataZoomOptionItem[] };
  dispatchAction: (payload: Record<string, unknown>) => void;
  setOption: (option: Record<string, unknown>, opts?: Record<string, unknown>) => void;
};

const getInsideDataZoom = (chart: TChartWithZoom): TDataZoomOptionItem | undefined => {
  const list = chart.getOption().dataZoom ?? [];
  return (
    list.find((item) => item.id === CHART_ZOOM_INSIDE_ID) ?? list.find((item) => item.type === 'inside') ?? list[0]
  );
};

export const getInsideDataZoomRange = (chart: TChartWithZoom): TChartZoomRange => {
  const dataZoom = getInsideDataZoom(chart);
  return {
    start: dataZoom?.start ?? 0,
    end: dataZoom?.end ?? 100,
  };
};

export const applyDataZoomRange = (chart: TChartWithZoom, range: TChartZoomRange): void => {
  chart.dispatchAction({
    type: 'dataZoom',
    dataZoomId: CHART_ZOOM_INSIDE_ID,
    start: range.start,
    end: range.end,
  });

  chart.setOption(
    {
      dataZoom: [
        {
          id: CHART_ZOOM_INSIDE_ID,
          start: range.start,
          end: range.end,
        },
      ],
    },
    { lazyUpdate: false },
  );
};

export const setDataZoomSelectActive = (chart: TChartWithZoom, active: boolean): void => {
  chart.dispatchAction({
    type: 'takeGlobalCursor',
    key: 'dataZoomSelect',
    dataZoomSelectActive: active,
  });
};

export const calcZoomRangeByFactor = (range: TChartZoomRange, factor: number): TChartZoomRange => {
  const center = (range.start + range.end) / 2;
  const span = Math.max((range.end - range.start) * factor, MIN_ZOOM_SPAN);
  let nextStart = center - span / 2;
  let nextEnd = center + span / 2;

  if (nextStart < 0) {
    nextEnd = Math.min(100, nextEnd - nextStart);
    nextStart = 0;
  }
  if (nextEnd > 100) {
    nextStart = Math.max(0, nextStart - (nextEnd - 100));
    nextEnd = 100;
  }

  nextStart = Math.max(0, Math.min(nextStart, 100 - MIN_ZOOM_SPAN));
  nextEnd = Math.max(nextStart + MIN_ZOOM_SPAN, Math.min(nextEnd, 100));

  return { start: nextStart, end: nextEnd };
};
